import { Sun, Moon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

const label = (isDark) => (isDark ? 'Switch to light theme' : 'Switch to dark theme');

/**
 * @param {'header' | 'bottomMobile' | 'bottomDesktop' | 'fixed'} variant
 * @param {boolean} [isDarkChrome] — header dark chrome (home / catalog / garages)
 */
export default function ThemeToggleButton({ variant = 'header', isDarkChrome = false }) {
  const { isDark, toggleTheme } = useTheme();
  const title = label(isDark);

  if (variant === 'fixed') {
    return (
      <motion.button
        type="button"
        onClick={toggleTheme}
        title={title}
        aria-label={title}
        initial={{ opacity: 0.95 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.96 }}
        className={`fixed z-[100] flex h-11 w-11 items-center justify-center rounded-full border shadow-lg backdrop-blur-sm transition-colors top-[max(0.75rem,env(safe-area-inset-top))] right-[max(0.75rem,env(safe-area-inset-right))] ${
          isDark
            ? 'border-slate-600 bg-slate-800/95 text-amber-300 hover:bg-slate-700'
            : 'border-gray-200 bg-white/95 text-slate-700 hover:bg-gray-50'
        }`}
      >
        {isDark ? <Sun className="h-5 w-5" strokeWidth={2} /> : <Moon className="h-5 w-5" strokeWidth={2} />}
      </motion.button>
    );
  }

  if (variant === 'bottomMobile') {
    return (
      <motion.button
        type="button"
        onClick={toggleTheme}
        title={title}
        aria-label={title}
        whileTap={{ scale: 0.9 }}
        className="sp-bottom-nav-item flex shrink-0 flex-col items-center justify-center rounded-2xl px-1 py-1.5 w-11 transition-colors text-sparepe-ink-3 hover:text-sparepe-accent-dk"
      >
        {isDark ? <Sun className="h-5 w-5" strokeWidth={2} /> : <Moon className="h-5 w-5" strokeWidth={2} />}
      </motion.button>
    );
  }

  if (variant === 'bottomDesktop') {
    return (
      <motion.button
        type="button"
        onClick={toggleTheme}
        title={title}
        aria-label={title}
        whileHover={{ y: -2, scale: 1.02 }}
        whileTap={{ scale: 0.95 }}
        className="sp-bottom-nav-item flex items-center gap-2 rounded-sparepe px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-sparepe-accent-lt/30 hover:text-sparepe-accent-dk"
      >
        {isDark ? <Sun className="h-4 w-4 shrink-0" strokeWidth={2} /> : <Moon className="h-4 w-4 shrink-0" strokeWidth={2} />}
        <span className="hidden xl:inline">Theme</span>
      </motion.button>
    );
  }

  /* header */
  return (
    <motion.button
      type="button"
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.96 }}
      onClick={toggleTheme}
      title={title}
      aria-label={title}
      className={
        isDarkChrome
          ? 'sparepe-header__icon-btn sparepe-header__iBtn'
          : 'p-2.5 rounded-xl transition-colors duration-200 hover:bg-gray-100 text-gray-600'
      }
    >
      {isDark ? (
        <Sun className={`${isDarkChrome ? 'text-base' : 'h-5 w-5'}`} strokeWidth={2} />
      ) : (
        <Moon className={`${isDarkChrome ? 'text-base' : 'h-5 w-5'}`} strokeWidth={2} />
      )}
    </motion.button>
  );
}
